import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../config/axios';
import { AuthContext } from '../context/AuthContext';
import { formatError } from '../utils/errorHandler';
import { formatTaskPriority } from '../utils/taskLabels';
import LoadingSpinner from '../components/LoadingSpinner';
import Modal from '../components/Modal';
import ColumnCustomizer from '../components/ColumnCustomizer';
import EmptyState from '../components/EmptyState';
import { useConfirm } from '../context/ConfirmContext';
import RecurrenceWeekdayPicker from '../components/RecurrenceWeekdayPicker';
import { formatTaskRecurrence, normalizeWeekdays } from '../utils/recurrenceWeekdays';
import './Tasks.css';
import './TaskPresets.css';

const TaskPresets = () => {
  const { t } = useTranslation(['presets', 'tasks', 'common']);
  const confirm = useConfirm();
  const { user } = useContext(AuthContext);
  const [taskPresets, setTaskPresets] = useState([]);
  const [presetChecklists, setPresetChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPreset, setEditingPreset] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    description: '',
    priority: 'medium',
    estimatedDuration: '',
    checklist: [],
    recurrenceEnabled: false,
    recurrenceIntervalDays: '',
    recurrenceWeekdays: []
  });
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [error, setError] = useState('');
  const [visibleColumns, setVisibleColumns] = useState({
    name: true,
    title: true,
    priority: true,
    duration: true,
    checklist: true,
    recurrence: true
  });
  const [sorting, setSorting] = useState({ column: null, direction: null });

  const presetColumnLabels = useMemo(() => ({
    name: t('common:name'),
    title: t('presets:columnTitle'),
    priority: t('tasks:columnPriority'),
    duration: t('presets:columnDuration'),
    checklist: t('tasks:columnChecklist'),
    recurrence: t('presets:columnRecurrence')
  }), [t]);

  useEffect(() => {
    fetchTaskPresets();
    if (user?.role === 'super_admin' || user?.role === 'admin') {
      fetchPresetChecklists();
    }
  }, [user]);

  const handleSort = (column) => {
    setSorting(prev => {
      if (prev.column === column) {
        if (prev.direction === 'asc') {
          return { column, direction: 'desc' };
        } else if (prev.direction === 'desc') {
          return { column: null, direction: null };
        }
      }
      return { column, direction: 'asc' };
    });
  };

  const renderSortableHeader = (column, label) => {
    const isSorted = sorting.column === column;
    const sortDirection = isSorted ? sorting.direction : null;
    
    return (
      <th 
        style={{ cursor: 'pointer', userSelect: 'none' }}
        onClick={() => handleSort(column)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span>{label}</span>
          {sortDirection === 'asc' && (
            <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>arrow_upward</span>
          )}
          {sortDirection === 'desc' && (
            <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>arrow_downward</span>
          )}
        </div>
      </th>
    );
  };

  const sortPresets = (presetList) => {
    if (!sorting.column || !sorting.direction) {
      return presetList;
    }

    return [...presetList].sort((a, b) => {
      let aValue, bValue;

      switch (sorting.column) {
        case 'name':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        case 'title':
          aValue = (a.title || '').toLowerCase();
          bValue = (b.title || '').toLowerCase();
          break;
        case 'priority':
          const priorityOrder = { high: 3, medium: 2, low: 1 };
          aValue = priorityOrder[a.priority] || 0;
          bValue = priorityOrder[b.priority] || 0;
          break;
        case 'duration':
          aValue = a.estimated_duration || 0;
          bValue = b.estimated_duration || 0;
          break;
        case 'checklist':
          const aChecklist = Array.isArray(a.checklist) ? a.checklist.length : 0;
          const bChecklist = Array.isArray(b.checklist) ? b.checklist.length : 0;
          aValue = aChecklist;
          bValue = bChecklist;
          break;
        case 'recurrence':
          aValue = a.recurrence_enabled ? 1 : 0;
          bValue = b.recurrence_enabled ? 1 : 0;
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string') {
        return sorting.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return sorting.direction === 'asc' 
          ? aValue - bValue
          : bValue - aValue;
      }
    });
  };

  const handleRowClick = (preset) => {
    handleEdit(preset);
  };

  const fetchTaskPresets = async () => {
    try {
      const response = await axios.get('/api/task-presets');
      const normalizedPresets = response.data.data.map(preset => {
        if (preset.checklist && Array.isArray(preset.checklist)) {
          preset.checklist = preset.checklist.map(item => {
            if (typeof item === 'string') {
              return { text: item, checked: false };
            }
            if (item && typeof item === 'object' && item !== null) {
              return {
                text: item.text || '',
                checked: item.checked || false
              };
            }
            return { text: String(item || ''), checked: false };
          });
        } else {
          preset.checklist = [];
        }
        return preset;
      });
      setTaskPresets(normalizedPresets);
    } catch (error) {
      console.error('Error fetching task presets:', error);
      setError(t('presets:failedFetchTaskPresets'));
    } finally {
      setLoading(false);
    }
  };

  const fetchPresetChecklists = async () => {
    try {
      const response = await axios.get('/api/preset-checklists');
      setPresetChecklists(response.data.data);
    } catch (error) {
      console.error('Error fetching preset checklists:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddChecklistItem = () => {
    if (newChecklistItem.trim()) {
      setFormData((prev) => ({
        ...prev,
        checklist: [...prev.checklist, newChecklistItem.trim()]
      }));
      setNewChecklistItem('');
    }
  };

  const handleRemoveChecklistItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      checklist: prev.checklist.filter((_, i) => i !== index)
    }));
  };

  const handlePresetChecklistChange = (e) => {
    const presetId = e.target.value;
    if (presetId) {
      const preset = presetChecklists.find(p => p.id === parseInt(presetId));
      if (preset && preset.items) {
        const checklistItems = Array.isArray(preset.items) 
          ? preset.items.map(item => {
              if (typeof item === 'string') {
                return { text: item, checked: false };
              }
              if (item && typeof item === 'object' && item !== null) {
                return {
                  text: item.text || item || '',
                  checked: item.checked || false
                };
              }
              return { text: String(item || ''), checked: false };
            })
          : [];
        setFormData(prev => ({
          ...prev,
          checklist: checklistItems
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        checklist: []
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError(t('presets:nameRequired'));
      return;
    }

    try {
      const payload = {
        name: formData.name,
        title: formData.title || null,
        description: formData.description || null,
        priority: formData.priority || 'medium',
        checklist: formData.checklist.map(item => {
          if (typeof item === 'string') {
            return { text: item, checked: false };
          }
          let textValue = '';
          if (item && typeof item === 'object') {
            textValue = item.text || '';
            if (!textValue) {
              textValue = String(item);
            }
          } else {
            textValue = String(item || '');
          }
          return { text: textValue, checked: item?.checked || false };
        }),
        recurrenceEnabled: formData.recurrenceEnabled || false
      };

      if (formData.estimatedDuration && formData.estimatedDuration.trim() !== '') {
        payload.estimatedDuration = parseInt(formData.estimatedDuration, 10);
      }

      if (formData.recurrenceEnabled) {
        const weekdays = normalizeWeekdays(formData.recurrenceWeekdays);
        if (weekdays.length === 0 && !(formData.recurrenceIntervalDays && String(formData.recurrenceIntervalDays).trim() !== '')) {
          setError(t('tasks:recurrenceWeekdaysRequired'));
          return;
        }
        payload.recurrenceWeekdays = weekdays;
        if (weekdays.length === 0 && formData.recurrenceIntervalDays) {
          payload.recurrenceIntervalDays = parseInt(formData.recurrenceIntervalDays, 10);
        }
      } else {
        payload.recurrenceWeekdays = [];
      }

      if (editingPreset) {
        await axios.put(`/api/task-presets/${editingPreset.id}`, payload);
      } else {
        await axios.post('/api/task-presets', payload);
      }
      handleCloseModal();
      fetchTaskPresets();
    } catch (error) {
      setError(
        formatError(error)
          || (editingPreset
            ? t('presets:failedUpdateTaskPreset')
            : t('presets:failedCreateTaskPreset'))
      );
    }
  };

  const handleEdit = (preset) => {
    setEditingPreset(preset);
    let checklist = [];
    if (preset.checklist) {
      if (Array.isArray(preset.checklist)) {
         checklist = preset.checklist.map(item => {
           if (typeof item === 'string') {
             return { text: item, checked: false };
           }
           let textValue = '';
           if (item && typeof item === 'object' && item !== null) {
             textValue = item.text || '';
             if (!textValue) {
               textValue = '';
             }
           } else {
             textValue = String(item || '');
           }
           return { text: textValue, checked: item?.checked || false };
         });
      }
    }
    setFormData({
      name: preset.name || '',
      title: preset.title || '',
      description: preset.description || '',
      priority: preset.priority || 'medium',
      estimatedDuration: preset.estimated_duration || '',
      checklist: checklist,
      recurrenceEnabled: preset.recurrence_enabled || false,
      recurrenceIntervalDays: preset.recurrence_interval_days || '',
      recurrenceWeekdays: normalizeWeekdays(preset.recurrence_weekdays)
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!(await confirm(t('presets:confirmDeleteTaskPreset'), { danger: true }))) {
      return false;
    }
    try {
      await axios.delete(`/api/task-presets/${id}`);
      fetchTaskPresets();
      return true;
    } catch (error) {
      setError(formatError(error) || t('presets:failedDeleteTaskPreset'));
      return false;
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPreset(null);
    setFormData({
      name: '',
      title: '',
      description: '',
      priority: 'medium',
      estimatedDuration: '',
      checklist: [],
      recurrenceEnabled: false,
      recurrenceIntervalDays: '',
      recurrenceWeekdays: []
    });
    setNewChecklistItem('');
    setError('');
  };

  const formatRecurrence = (preset) => formatTaskRecurrence(preset, t);

  if (loading) {
    return <LoadingSpinner />;
  }

  const sortedPresets = sortPresets(taskPresets);

  return (
    <div className="task-presets">
      <div className="container container--wide main-content">
        <div className="page-header">
          <h1>{t('presets:taskPresetsPageTitle')}</h1>
          <div className="page-header-actions">
            <ColumnCustomizer
              columnLabels={presetColumnLabels}
              visibleColumns={visibleColumns}
              onChange={(key, checked) => setVisibleColumns((prev) => ({ ...prev, [key]: checked }))}
            />
            {(user?.role === 'super_admin' || user?.role === 'admin') && (
              <button type="button" className="btn btn-primary tasks-header-create-btn" onClick={() => setShowModal(true)}>
                <span className="material-symbols-outlined" aria-hidden="true">add</span>
                {t('presets:createTaskPreset')}
              </button>
            )}
          </div>
        </div>

        {error && <div className="error">{error}</div>}

        {sortedPresets.length > 0 ? (
          <>
            <div className="tasks-list-table-wrap tasks-list-table-desktop">
              <table className="table tasks-list-table">
                <thead>
                  <tr>
                    {visibleColumns.name && renderSortableHeader('name', presetColumnLabels.name)}
                    {visibleColumns.title && renderSortableHeader('title', presetColumnLabels.title)}
                    {visibleColumns.priority && renderSortableHeader('priority', presetColumnLabels.priority)}
                    {visibleColumns.duration && renderSortableHeader('duration', presetColumnLabels.duration)}
                    {visibleColumns.checklist && renderSortableHeader('checklist', presetColumnLabels.checklist)}
                    {visibleColumns.recurrence && renderSortableHeader('recurrence', presetColumnLabels.recurrence)}
                  </tr>
                </thead>
                <tbody>
                  {sortedPresets.map((preset) => {
                    const checklist = Array.isArray(preset.checklist) ? preset.checklist : [];
                    const checklistCount = checklist.length;
                    return (
                      <tr
                        key={preset.id}
                        className="tasks-list-table-row"
                        onClick={() => handleRowClick(preset)}
                      >
                        {visibleColumns.name && (
                          <td className="tasks-list-table-title">{preset.name}</td>
                        )}
                        {visibleColumns.title && <td>{preset.title || t('tasks:emptyCell')}</td>}
                        {visibleColumns.priority && (
                          <td>{formatTaskPriority(preset.priority || 'medium', t)}</td>
                        )}
                        {visibleColumns.duration && (
                          <td>
                            {preset.estimated_duration
                              ? t('presets:durationMinutes', { count: preset.estimated_duration })
                              : t('tasks:emptyCell')}
                          </td>
                        )}
                        {visibleColumns.checklist && (
                          <td>{t('presets:itemCount', { count: checklistCount })}</td>
                        )}
                        {visibleColumns.recurrence && (
                          <td>{formatRecurrence(preset)}</td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="tasks-card-grid tasks-list-cards-mobile">
              {sortedPresets.map((preset) => (
                <article
                  key={preset.id}
                  className="tasks-redesign-card"
                  onClick={() => handleRowClick(preset)}
                >
                  <div className="tasks-redesign-card-body">
                    <div className="tasks-redesign-card-top">
                      <h3 className="tasks-redesign-card-title">{preset.name}</h3>
                    </div>
                    <div className="tasks-redesign-card-meta">
                      {preset.title && (
                        <span className="tasks-redesign-meta-item">{preset.title}</span>
                      )}
                      <span className="tasks-redesign-meta-item">
                        <span className={`tasks-priority-dot tasks-priority-dot--${preset.priority || 'medium'}`} aria-hidden="true" />
                        {formatTaskPriority(preset.priority || 'medium', t)}
                      </span>
                    </div>
                    {(user?.role === 'super_admin' || user?.role === 'admin') && (
                      <div className="tasks-redesign-card-actions" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDelete(preset.id)}
                        >
                          {t('common:delete')}
                        </button>
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </>
        ) : (
          <EmptyState
            icon="bookmark"
            title={t('presets:noTaskPresetsTitle')}
            description={t('presets:noTaskPresetsDescription')}
            actionLabel={(user?.role === 'super_admin' || user?.role === 'admin') ? t('presets:createTaskPreset') : undefined}
            onAction={(user?.role === 'super_admin' || user?.role === 'admin') ? () => setShowModal(true) : undefined}
          />
        )}

        <Modal
          isOpen={showModal}
          onClose={handleCloseModal}
          title={editingPreset ? t('presets:editTaskPreset') : t('presets:createTaskPreset')}
          className="task-presets-modal"
        >
          <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>{t('presets:presetNameLabel')}</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder={t('presets:presetNamePlaceholder')}
                  />
                  <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                    {t('presets:presetNameHint')}
                  </small>
                </div>
                <div className="form-group">
                  <label>{t('presets:defaultTaskTitleLabel')}</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder={t('presets:defaultTaskTitlePlaceholder')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('presets:defaultDescriptionLabel')}</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="3"
                    placeholder={t('presets:defaultDescriptionPlaceholder')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('presets:defaultPriorityLabel')}</label>
                  <select
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                  >
                    <option value="low">{formatTaskPriority('low', t)}</option>
                    <option value="medium">{formatTaskPriority('medium', t)}</option>
                    <option value="high">{formatTaskPriority('high', t)}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('tasks:estimatedDurationLabel')}</label>
                  <input
                    type="number"
                    name="estimatedDuration"
                    value={formData.estimatedDuration}
                    onChange={handleChange}
                    min="0"
                    placeholder={t('presets:optional')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('tasks:checklistLabel')}</label>
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ fontSize: '14px', marginBottom: '5px', display: 'block' }}>
                      {t('tasks:usePresetChecklistLabel')}
                    </label>
                    <select
                      onChange={handlePresetChecklistChange}
                      style={{ width: '100%', padding: '8px', marginBottom: '10px' }}
                    >
                      <option value="">{t('tasks:selectPresetChecklist')}</option>
                      {presetChecklists.map((preset) => (
                        <option key={preset.id} value={preset.id}>
                          {preset.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="checklist-editor">
                    {formData.checklist.map((item, index) => {
                      let itemText = '';
                      if (typeof item === 'string') {
                        itemText = item;
                      } else if (item && typeof item === 'object' && item !== null) {
                        itemText = item.text || '';
                        if (!itemText && item.text !== undefined) {
                          itemText = String(item.text);
                        } else if (!itemText) {
                          itemText = '';
                        }
                      } else {
                        itemText = String(item || '');
                      }
                      if (typeof itemText !== 'string') {
                        itemText = String(itemText || '');
                      }
                      return (
                        <div key={index} className="checklist-item">
                          <span className="checklist-item-name">{itemText}</span>
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => handleRemoveChecklistItem(index)}
                          >
                            {t('tasks:removeChecklistItem')}
                          </button>
                        </div>
                      );
                    })}
                    <div className="add-checklist-item">
                      <input
                        type="text"
                        value={newChecklistItem}
                        onChange={(e) => setNewChecklistItem(e.target.value)}
                        placeholder={t('tasks:placeholderChecklistItem')}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddChecklistItem();
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={handleAddChecklistItem}
                      >
                        {t('tasks:addChecklistItem')}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={{ margin: 0 }}>{t('presets:defaultRecurrenceEnabledLabel')}</label>
                    <div 
                      className={`task-toggle-switch ${formData.recurrenceEnabled ? 'active' : ''}`}
                      onClick={() => setFormData(prev => ({ ...prev, recurrenceEnabled: !prev.recurrenceEnabled }))}
                    >
                      <div className="task-toggle-knob"></div>
                    </div>
                  </div>
                </div>
                {formData.recurrenceEnabled && (
                  <div className="form-group">
                    <label>{t('tasks:recurrenceWeekdaysLabel')}</label>
                    <RecurrenceWeekdayPicker
                      idPrefix="preset-recurrence-day"
                      value={formData.recurrenceWeekdays || []}
                      onChange={(recurrenceWeekdays) => setFormData((prev) => ({
                        ...prev,
                        recurrenceWeekdays,
                        recurrenceIntervalDays: ''
                      }))}
                    />
                    <small className="recurrence-weekday-hint">{t('tasks:recurrenceWeekdaysHint')}</small>
                  </div>
                )}
                {error && <div className="error">{error}</div>}
                <div className="modal-actions">
                  <button type="submit" className="btn btn-primary">
                    {editingPreset ? t('common:update') : t('common:create')}
                  </button>
                  {editingPreset && (
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={async () => {
                        if (await handleDelete(editingPreset.id)) {
                          handleCloseModal();
                        }
                      }}
                    >
                      {t('common:delete')}
                    </button>
                  )}
                  <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                    {t('common:cancel')}
                  </button>
                </div>
              </form>
        </Modal>
      </div>
    </div>
  );
};

export default TaskPresets;
